import { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RestaurantMenu from "@mui/icons-material/RestaurantMenu";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LockIcon from "@mui/icons-material/Lock";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { State, City } from "india-state-city";
import { registerUser } from "../../redux/slices/authSlice";
import { toast } from "react-toastify";
import PageTitle from "../../components/PageTitle";

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth || {});

  const [form, setForm] = useState({
    restaurantName: "",
    ownerName: "",
    mobile: "",
    email: "",
    whatsappNumber: "",
    password: "",
    state: "",
    city: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const steps = ["Restaurant Info", "Contact Details", "Security"];

  // ✅ All Indian states
  const allStates = useMemo(() => State.getAllStates(), []);

  // ✅ Cities for selected state
  const citiesOfSelectedState = useMemo(() => {
    if (!form.state) return [];
    return City.getCitiesOfState(form.state);
  }, [form.state]);

  // ✅ Find selected state object (for Autocomplete value)
  const selectedStateObj = useMemo(
    () => allStates.find((s) => s.isoCode === form.state) || null,
    [allStates, form.state],
  );

  // ✅ Find selected city object
  const selectedCityObj = useMemo(
    () => citiesOfSelectedState.find((c) => c.name === form.city) || null,
    [citiesOfSelectedState, form.city],
  );

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(registerUser(form));
    if (result.type === "auth/register/fulfilled") {
      toast.success("Registration successful");
      navigate("/owner/dashboard");
    } else {
      toast.error(result.payload || result.error?.message);
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return form.restaurantName && form.ownerName && form.state && form.city;
      case 1:
        return form.mobile && form.whatsappNumber;
      case 2:
        return form.password && form.password.length >= 6;
      default:
        return false;
    }
  };

  // Common styling for outlined inputs
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "&:hover fieldset": { borderColor: "var(--primary)" },
      "&.Mui-focused fieldset": { borderColor: "var(--primary)" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "var(--primary)" },
  };

  return (
    <>
      <PageTitle
        title="Register Your Restaurant – RestaurantWala"
        description="Create your RestaurantWala account and start managing your restaurant with digital menus, orders, billing, staff and more."
        keywords="restaurant software registration, restaurant management software, register restaurant, RestaurantWala registration"
      />
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--primary-light) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          py: 4,
        }}
      >
        {/* Back to Home Button */}
        <Box sx={{ position: "absolute", top: 24, left: 24, zIndex: 10 }}>
          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: "#fff",
              backgroundColor: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              borderRadius: "50px",
              px: 2.5,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.9rem",
              border: "1px solid rgba(255,255,255,0.2)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.25)",
                transform: "scale(1.05)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Back to Home
          </Button>
        </Box>

        {/* Decorative Background */}
        {[
          { size: 350, top: "-12%", left: "-8%", opacity: 0.07 },
          { size: 450, bottom: "-18%", right: "-10%", opacity: 0.07 },
          { size: 220, top: "40%", right: "8%", opacity: 0.05 },
        ].map((circle, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: circle.size,
              height: circle.size,
              borderRadius: "50%",
              bgcolor: "white",
              opacity: circle.opacity,
              top: circle.top,
              left: circle.left,
              bottom: circle.bottom,
              right: circle.right,
            }}
          />
        ))}

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={24}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 4,
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
          >
            {/* Logo & Title */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                  boxShadow: "var(--shadow-orange-hover)",
                }}
              >
                <RestaurantMenu sx={{ fontSize: 44, color: "white" }} />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "1.5rem", sm: "1.9rem" },
                  background:
                    "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 0.5,
                }}
              >
                Start Your Journey
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "var(--text-light)", fontSize: "0.95rem" }}
              >
                Register your restaurant in 3 simple steps
              </Typography>
            </Box>

            {/* Stepper */}
            <Stepper
              activeStep={activeStep}
              sx={{
                mb: 4,
                "& .MuiStepLabel-root .Mui-completed": {
                  color: "var(--primary)",
                },
                "& .MuiStepLabel-root .Mui-active": {
                  color: "var(--primary)",
                },
                "& .MuiStepIcon-root.Mui-completed": {
                  color: "var(--primary)",
                },
                "& .MuiStepIcon-root.Mui-active": {
                  color: "var(--primary)",
                },
              }}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Error Alert */}
            {error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  "& .MuiAlert-icon": { color: "var(--primary)" },
                }}
              >
                {error}
              </Alert>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit}>
              {/* Step 0: Restaurant Info */}
              {activeStep === 0 && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  <TextField
                    fullWidth
                    label="Restaurant Name"
                    name="restaurantName"
                    value={form.restaurantName}
                    onChange={handleChange}
                    required
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <RestaurantMenu sx={{ color: "var(--primary)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Owner Name"
                    name="ownerName"
                    value={form.ownerName}
                    onChange={handleChange}
                    required
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ color: "var(--primary)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* ✅ State Autocomplete (searchable) */}
                  <Autocomplete
                    options={allStates}
                    getOptionLabel={(option) => option.name || ""}
                    value={selectedStateObj}
                    onChange={(_, newValue) => {
                      setForm({
                        ...form,
                        state: newValue ? newValue.isoCode : "",
                        city: "",
                      });
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.isoCode === value.isoCode
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        placeholder="Search state..."
                        required
                        sx={inputSx}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <LocationOnIcon
                                  sx={{ color: "var(--primary)" }}
                                />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  {/* ✅ City Autocomplete (searchable + depends on state) */}
                  <Autocomplete
                    options={citiesOfSelectedState}
                    getOptionLabel={(option) => option.name || ""}
                    value={selectedCityObj}
                    onChange={(_, newValue) => {
                      setForm({
                        ...form,
                        city: newValue ? newValue.name : "",
                      });
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.name === value.name
                    }
                    disabled={!form.state}
                    noOptionsText={
                      form.state
                        ? "No cities found"
                        : "Select a state first"
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        placeholder={
                          form.state ? "Search city..." : "Select state first"
                        }
                        required
                        sx={inputSx}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <LocationOnIcon
                                  sx={{ color: "var(--primary)" }}
                                />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              )}

              {/* Step 1: Contact Details */}
              {activeStep === 1 && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  <TextField
                    fullWidth
                    label="Mobile Number"
                    name="mobile"
                    type="tel"
                    value={form.mobile}
                    onChange={handleChange}
                    required
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIphoneIcon sx={{ color: "var(--primary)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="WhatsApp Number"
                    name="whatsappNumber"
                    type="tel"
                    value={form.whatsappNumber}
                    onChange={handleChange}
                    required
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WhatsAppIcon sx={{ color: "#25D366" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: "var(--primary)" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}

              {/* Step 2: Security */}
              {activeStep === 2 && (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  <TextField
                    fullWidth
                    label="Create Password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange}
                    required
                    helperText="Minimum 6 characters"
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: "var(--primary)" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            sx={{
                              "&:hover": { color: "var(--primary)" },
                            }}
                          >
                            {showPassword ? (
                              <VisibilityOffIcon />
                            ) : (
                              <VisibilityIcon />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Alert
                    severity="info"
                    sx={{
                      borderRadius: 2,
                      "& .MuiAlert-icon": { color: "var(--primary)" },
                    }}
                  >
                    Your data is encrypted and secure. We'll never share your
                    information.
                  </Alert>
                </Box>
              )}

              {/* Navigation Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 4,
                }}
              >
                <Button
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    color: "var(--primary)",
                    "&:hover": { bgcolor: "var(--bg-primary-soft)" },
                  }}
                >
                  Back
                </Button>

                {activeStep === steps.length - 1 ? (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !isStepValid()}
                    endIcon={
                      loading ? (
                        <CircularProgress
                          size={20}
                          sx={{ color: "white" }}
                        />
                      ) : (
                        <RocketLaunchIcon />
                      )
                    }
                    sx={{
                      px: 4,
                      py: 1.2,
                      background:
                        "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                        transform: "translateY(-3px)",
                        boxShadow: "var(--shadow-orange-hover)",
                      },
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "var(--shadow-orange)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {loading ? "Creating..." : "Launch Restaurant"}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    disabled={!isStepValid()}
                    sx={{
                      px: 4,
                      background:
                        "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)",
                        transform: "translateY(-2px)",
                        boxShadow: "var(--shadow-orange)",
                      },
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "var(--shadow-orange)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>

            {/* Divider */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                my: 3,
              }}
            >
              <Box sx={{ flex: 1, height: 1, bgcolor: "var(--border)" }} />
              <Box sx={{ flex: 1, height: 1, bgcolor: "var(--border)" }} />
            </Box>

            {/* Login Link */}
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "var(--text-light)" }}>
                Already have an account?{" "}
                <Link
                  to="/login"
                  style={{
                    color: "var(--primary)",
                    textDecoration: "none",
                    fontWeight: 700,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "var(--primary-dark)";
                    e.target.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "var(--primary)";
                    e.target.style.textDecoration = "none";
                  }}
                >
                  Login Here
                </Link>
              </Typography>
            </Box>
          </Paper>

          
        </Container>
          {/* Footer */}
        <Typography
            variant="caption"
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  color: "rgba(255,255,255,0.75)",
                  fontSize: 11,
                  fontWeight: 500,
                  zIndex: 1,
                }}
          >
            © {new Date().getFullYear()} RestaurantWala • Your Digital Restaurant Partner
          </Typography>
      </Box>
    </>
  );
};

export default Register;